import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Loader2, Upload, Trash2, Download, RefreshCw, FileText, Shield, X,
} from "lucide-react";
import api from "@/lib/api";

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
const DOC_TYPES = ["passport", "national_id", "residence_card", "other"];

function formatBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Identity document manager modal.
 * Props:
 *   userId    - target user id (Agent or Master Agent)
 *   username  - display name of the target
 *   isAdmin   - whether the actor is MASTER_ADMIN (unlocks delete)
 *   onClose() - close callback
 */
export default function IdentityDocumentModal({ userId, username, isAdmin, onClose }) {
  const { t } = useTranslation();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [docType, setDocType] = useState("passport");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/identity-documents/${userId}`);
      setDoc(data.document || null);
    } catch (e) {
      // 404-not-found is fine (no document yet). Other errors surface.
      if (e.response?.status !== 404) {
        toast.error(e.response?.data?.detail || t("id_doc.load_failed", "Could not load document"));
      }
      setDoc(null);
    } finally { setLoading(false); }
  }, [userId, t]);

  useEffect(() => { load(); }, [load]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_MIME.includes(f.type)) {
      toast.error(t("id_doc.bad_type", "Unsupported file type. Use JPG, PNG or PDF."));
      e.target.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error(t("id_doc.too_large", "File is larger than 5 MB."));
      e.target.value = "";
      return;
    }
    setSelectedFile(f);
    if (f.type.startsWith("image/")) {
      const url = URL.createObjectURL(f);
      setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    } else {
      setPreviewUrl(null);
    }
  };

  const upload = async () => {
    if (!selectedFile) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", selectedFile);
      fd.append("doc_type", docType);
      const { data } = await api.post(`/identity-documents/${userId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDoc(data.document);
      setSelectedFile(null);
      if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
      if (inputRef.current) inputRef.current.value = "";
      toast.success(t("id_doc.uploaded", "Document uploaded"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("id_doc.upload_failed", "Upload failed"));
    } finally { setBusy(false); }
  };

  const downloadDoc = async (openInline = false) => {
    setBusy(true);
    try {
      const res = await api.get(`/identity-documents/${userId}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: doc?.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      if (openInline) {
        // Open in a new tab for viewing; browser handles PDF/image inline.
        const win = window.open(url, "_blank", "noopener,noreferrer");
        if (!win) toast.error(t("id_doc.popup_blocked", "Please allow pop-ups to view the document"));
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = doc?.filename || "identity-document";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Release the blob after a moment; new-tab needs it alive briefly.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("id_doc.download_failed", "Download failed"));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(t("id_doc.delete_confirm", "Permanently delete this identity document?"))) return;
    setBusy(true);
    try {
      await api.delete(`/identity-documents/${userId}`);
      setDoc(null);
      toast.success(t("id_doc.deleted", "Document deleted"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("id_doc.delete_failed", "Delete failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" data-testid="id-doc-modal">
      <div className="bg-white rounded-md shadow-xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#0F62FE]" />
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {t("id_doc.title", "Identity document")}
            </h2>
          </div>
          <button data-testid="id-doc-close" onClick={onClose} className="p-1 text-slate-500 hover:text-slate-900">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="text-xs text-slate-500">
            {t("id_doc.subtitle", "Manage the identification document for")} <span className="font-semibold text-slate-800">{username}</span>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading")}</div>
          ) : doc ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="id-doc-current">
              <div className="flex items-start gap-3">
                <FileText className="w-8 h-8 text-[#0F62FE] flex-shrink-0" strokeWidth={1.6} />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate">{doc.filename}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {t(`id_doc.type_${doc.doc_type}`, doc.doc_type)} · {formatBytes(doc.size)} · {new Date(doc.uploaded_at).toLocaleString()}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {t("id_doc.uploaded_by", "Uploaded by")}: {doc.uploader_username || "—"}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  data-testid="id-doc-view"
                  onClick={() => downloadDoc(true)}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <FileText className="w-4 h-4" /> {t("id_doc.view", "View")}
                </button>
                <button
                  data-testid="id-doc-download"
                  onClick={() => downloadDoc(false)}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <Download className="w-4 h-4" /> {t("id_doc.download", "Download")}
                </button>
                <button
                  data-testid="id-doc-replace-trigger"
                  onClick={() => inputRef.current?.click()}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <RefreshCw className="w-4 h-4" /> {t("id_doc.replace", "Replace")}
                </button>
                {isAdmin && (
                  <button
                    data-testid="id-doc-delete"
                    onClick={remove}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-rose-300 bg-white text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" /> {t("id_doc.delete", "Delete")}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center" data-testid="id-doc-empty">
              <FileText className="w-8 h-8 text-slate-400 mx-auto mb-2" strokeWidth={1.6} />
              <div className="text-sm text-slate-600">{t("id_doc.none_yet", "No identity document uploaded yet.")}</div>
            </div>
          )}

          {/* Upload / replace form */}
          <div className="rounded-lg border border-slate-200 p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#0F62FE] mb-2">
              {doc ? t("id_doc.replace_section", "Replace document") : t("id_doc.upload_section", "Upload document")}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">
                  {t("id_doc.doc_type_label", "Document type")}
                </label>
                <select
                  data-testid="id-doc-type"
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  className="h-10 w-full rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
                >
                  {DOC_TYPES.map((k) => (
                    <option key={k} value={k}>{t(`id_doc.type_${k}`, k)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">
                  {t("id_doc.file_label", "File (JPG, PNG, PDF · max 5 MB)")}
                </label>
                <input
                  ref={inputRef}
                  data-testid="id-doc-file"
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                  onChange={pickFile}
                  className="block w-full text-sm text-slate-700 file:mr-3 file:h-9 file:px-3 file:rounded-lg file:border-0 file:bg-[#0F62FE] file:text-white file:text-xs file:font-semibold hover:file:bg-[#0050E6]"
                />
              </div>
            </div>
            {previewUrl && (
              <div className="mt-3">
                <img src={previewUrl} alt="preview" className="max-h-40 rounded-md border border-slate-200 mx-auto" />
              </div>
            )}
            {selectedFile && !previewUrl && (
              <div className="mt-3 text-xs text-slate-600">
                <FileText className="inline w-4 h-4 mr-1 align-text-bottom" />
                {selectedFile.name} · {formatBytes(selectedFile.size)}
              </div>
            )}
            <div className="mt-3 flex justify-end">
              <button
                data-testid="id-doc-upload-submit"
                onClick={upload}
                disabled={busy || !selectedFile}
                className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {doc ? t("id_doc.replace", "Replace") : t("id_doc.upload", "Upload")}
              </button>
            </div>
          </div>

          <div className="text-[11px] text-slate-500 flex items-start gap-1.5">
            <Shield className="w-3.5 h-3.5 mt-0.5" />
            <span>{t("id_doc.security_note", "Documents are encrypted at rest and never exposed via public links. All access is audited.")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
